import type { SupportedLanguage } from "@/lib/i18n/languages";
import { PROTOCOL_COPY, type ProtocolCopy } from "@/lib/protocol-copy";

type AdditionalProtocolLanguage = Exclude<SupportedLanguage, "en" | "de">;

const ADDITIONAL_PROTOCOL_COPY: Record<
  AdditionalProtocolLanguage,
  ProtocolCopy
> = {
  fr: {
    greeting: "Bonjour",
    title: "État des lieux",
    subtitle:
      "Documentez l’état de votre chambre et des espaces communs en quelques étapes.",
    bulletDuration: "Environ 8 à 12 min",
    bulletPause: "Vous pouvez faire une pause à tout moment",
    bulletStorage: "Les photos sont enregistrées en toute sécurité",
    yourHome: "Votre logement",
    fallbackHome: "Votre logement coliviq",
    room: "Chambre",
    noBooking:
      "Aucune réservation active trouvée. Veuillez contacter l’assistance.",
    kindMoveIn: "Entrée",
    kindMoveInSub: "À remplir lors de votre arrivée",
    kindMoveOut: "Sortie",
    kindMoveOutSub: "À remplir avant votre départ",
    badgeRecommended: "RECOMMANDÉ AUJOURD’HUI",
    pauseHint:
      "Vous pouvez faire une pause à tout moment — votre progression est enregistrée.",
    cta: "Commencer",
    errorCreate: "Impossible de créer l’état des lieux",
    pause: "Pause",
    back: "Retour",
    continue: "Continuer",
    step: "Étape",
    of: "sur",
    yourProgress: "Votre progression",
    sectionsDone: "{done}/{total} sections terminées",
    stepKeys: "Clés",
    stepPhotos: "Photos",
    stepInventory: "Inventaire",
    stepCommon: "Espaces communs",
    stepSign: "Signature",
    photosTitle: "Photos de la chambre",
    photosSubtitle:
      "Photographiez tous les murs, le sol, les fenêtres et le plafond. Minimum {min} photos.",
    photosCount: "{n}/{max} photos",
    photosMin: "min. {n} requises",
    photosCta: "Continuer vers l’inventaire",
    continueHintPhotos:
      "Ajoutez encore {n} photo(s) de la chambre pour continuer.",
    continueHintKeys: "Ajoutez au moins {n} photo des clés pour continuer.",
    inventoryTitle: "Vérifier l’inventaire",
    inventorySubtitle: "Une photo et un état sont requis pour chaque élément.",
    conditionGood: "Bon",
    conditionWear: "Traces d’usure",
    conditionDamaged: "Endommagé",
    conditionLabel: "État",
    conditionChoose: "Choisir",
    inventoryCta: "Continuer vers les espaces communs",
    missingConditions: "{n} état(s) manquant(s)",
    missingPhotos: "Encore {n} photo(s) manquante(s)",
    inventoryCheckedCount: "{done}/{total} vérifiés",
    defectDescription: "Description du dommage",
    severityLow: "Faible",
    severityMedium: "Moyen",
    severityHigh: "Important",
    commonTitle: "Espaces communs",
    commonSubtitle:
      "Au moins {min} photos par espace — photographiez tous les dommages visibles.",
    commonAreaKitchen: "Cuisine",
    commonAreaBath: "Salle de bains",
    commonAreaHallway: "Couloir",
    commonAreaOther: "Autre",
    reportDefect: "Signaler un dommage",
    commonAreaCount: "{n}/{total} espaces terminés",
    commonCta: "Continuer vers la signature",
    continueHintCommon:
      "Ajoutez encore {n} photo(s) pour {area} afin de continuer.",
    reportedDefects: "Dommages signalés",
    reportDamageTitle: "Signaler un dommage",
    photoOfDamage: "Photo du dommage",
    capturePhoto: "Prendre une photo",
    description: "Description",
    descriptionPlaceholder: "p. ex. rayure sur le bureau, environ 10 cm.",
    severity: "Gravité",
    cancel: "Annuler",
    saveDamage: "Enregistrer le dommage",
    saveFailed: "Échec de l’enregistrement",
    describeShort: "Veuillez saisir une courte description.",
    signTitle: "Confirmation",
    signSubtitle:
      "Vérifiez les informations et signez numériquement pour finaliser l’état des lieux.",
    summary: "Récapitulatif",
    object: "Logement",
    roomLabel: "Chambre",
    date: "Date",
    tenant: "Locataire",
    operation: "Opération",
    moveIn: "Entrée",
    moveOut: "Sortie",
    signature: "Signature",
    clear: "Effacer",
    signTerms:
      "En signant, vous confirmez que toutes les informations sont correctes. coliviq et le propriétaire reçoivent une copie par e-mail. Les conditions d’utilisation et la politique de confidentialité s’appliquent.",
    finalizeCta: "Finaliser l’état des lieux",
    pleaseSign: "Veuillez signer pour continuer.",
    doneTitle: "État des lieux envoyé",
    doneSubtitle:
      "Votre état des lieux a été enregistré. Une copie a été envoyée par e-mail à :",
    refLabel: "RÉF",
    downloadPdf: "Télécharger le PDF",
    pdfSoon: "L’export PDF sera bientôt disponible.",
    pdfSoonDesc:
      "Nous vous informerons dès que le téléchargement sera disponible.",
    toHome: "Retour au tableau de bord",
    notFound: "État des lieux introuvable.",
    photoUploadFailed: "La photo n’a pas pu être envoyée",
    photoDeleteFailed: "La photo n’a pas pu être supprimée",
    tipStart: "Conseil avant de commencer",
    tipBody:
      "Veillez à avoir un bon éclairage et vérifiez votre espace de stockage — chaque photo fait environ 2 Mo.",
    activeBadge: "Actif",
    doneBadge: "Terminé",
    statusSubmitted: "Envoyé",
    statusDraft: "En cours",
    statusNotStarted: "Pas encore commencé",
    statusLocked: "Disponible plus tard",
    statusExpired: "Accepté automatiquement",
    statusClosed: "Fermé",
    submittedOn: "Envoyé le {date}",
    lockedUntil: "Disponible à partir du {date}",
    lockedHint:
      "L’état des lieux de sortie sera disponible 14 jours avant votre départ.",
    moveInLockedHint:
      "L’état des lieux d’entrée sera disponible le jour de votre arrivée pendant 48 heures.",
    moveInExpiredHint:
      "Le délai de 48 heures est terminé ; la chambre est considérée comme acceptée conformément à votre contrat de location.",
    moveOutExpiredHint:
      "Le délai de sortie s’est terminé sans état des lieux complété.",
    closedHint: "L’état des lieux est actuellement fermé.",
    viewPdf: "Voir le PDF",
    viewPdfFailed:
      "Impossible d’ouvrir le PDF. Veuillez réessayer dans un instant.",
    loadProtocolFailed:
      "Impossible de charger votre état des lieux enregistré. Vérifiez votre connexion et réessayez.",
    noPdfAvailable: "Aucun PDF disponible",
    resumeDraft: "Continuer",
    startMoveIn: "Commencer l’état des lieux d’entrée",
    startMoveOut: "Commencer l’état des lieux de sortie",
    actionNeeded: "Action requise",
    actionNeededMoveInHint:
      "Remplissez votre état des lieux d’entrée · arrivée le {date}",
    actionNeededMoveOutHint:
      "Remplissez votre état des lieux de sortie · départ le {date}",
    actionNeededMoveInHintNoDate: "Remplissez votre état des lieux d’entrée",
    actionNeededMoveOutHintNoDate: "Remplissez votre état des lieux de sortie",
    actionNeededUpcomingHint:
      "Ouvre le {date} — remplissez-le le jour de votre arrivée",
    actionNeededAria: "L’état des lieux requiert votre attention",
    bothDone: "Tout est terminé ! Les deux états des lieux ont été envoyés.",
    moveInDoneTitle: "État des lieux d’entrée",
    moveOutDoneTitle: "État des lieux de sortie",
    nextStep: "Suivant",
    refLabelInline: "Réf",
    doneTitleKind: "État des lieux de {kind} envoyé",
  },
  ar: {
    greeting: "مرحبًا",
    title: "محضر تسليم السكن",
    subtitle: "وثّق حالة غرفتك والمساحات المشتركة في بضع خطوات.",
    bulletDuration: "يستغرق نحو 8–12 دقيقة",
    bulletPause: "يمكنك التوقف مؤقتًا في أي وقت",
    bulletStorage: "تُحفظ الصور بأمان",
    yourHome: "منزلك",
    fallbackHome: "منزلك لدى coliviq",
    room: "الغرفة",
    noBooking: "لم يتم العثور على حجز نشط. يرجى التواصل مع الدعم.",
    kindMoveIn: "الانتقال إلى السكن",
    kindMoveInSub: "يُملأ عند الانتقال إلى السكن",
    kindMoveOut: "مغادرة السكن",
    kindMoveOutSub: "يُملأ قبل المغادرة",
    badgeRecommended: "موصى به اليوم",
    pauseHint: "يمكنك التوقف مؤقتًا في أي وقت — يتم حفظ تقدمك.",
    cta: "ابدأ الآن",
    errorCreate: "تعذر إنشاء المحضر",
    pause: "إيقاف مؤقت",
    back: "رجوع",
    continue: "متابعة",
    step: "الخطوة",
    of: "من",
    yourProgress: "تقدمك",
    sectionsDone: "اكتمل {done} من {total} أقسام",
    stepKeys: "المفاتيح",
    stepPhotos: "الصور",
    stepInventory: "الموجودات",
    stepCommon: "المساحات المشتركة",
    stepSign: "التوقيع",
    photosTitle: "صور الغرفة",
    photosSubtitle:
      "صوّر جميع الجدران والأرضية والنوافذ والسقف. الحد الأدنى {min} صور.",
    photosCount: "{n}/{max} صور",
    photosMin: "مطلوب {n} على الأقل",
    photosCta: "المتابعة إلى الموجودات",
    continueHintPhotos: "حمّل {n} صورة/صور إضافية للغرفة للمتابعة.",
    continueHintKeys: "حمّل صورة واحدة على الأقل للمفاتيح للمتابعة.",
    inventoryTitle: "فحص الموجودات",
    inventorySubtitle: "يلزم إرفاق صورة وتحديد الحالة لكل عنصر.",
    conditionGood: "جيدة",
    conditionWear: "آثار استخدام",
    conditionDamaged: "متضررة",
    conditionLabel: "الحالة",
    conditionChoose: "اختر",
    inventoryCta: "المتابعة إلى المساحات المشتركة",
    missingConditions: "{n} حالة غير محددة",
    missingPhotos: "ما زالت {n} صورة/صور مفقودة",
    inventoryCheckedCount: "تم فحص {done}/{total}",
    defectDescription: "وصف الضرر",
    severityLow: "بسيط",
    severityMedium: "متوسط",
    severityHigh: "شديد",
    commonTitle: "المساحات المشتركة",
    commonSubtitle:
      "{min} صور على الأقل لكل مساحة — صوّر جميع الأضرار الظاهرة.",
    commonAreaKitchen: "المطبخ",
    commonAreaBath: "الحمّام",
    commonAreaHallway: "الممر",
    commonAreaOther: "أخرى",
    reportDefect: "الإبلاغ عن ضرر",
    commonAreaCount: "اكتملت {n}/{total} مساحات",
    commonCta: "المتابعة إلى التوقيع",
    continueHintCommon: "حمّل {n} صورة/صور إضافية لـ {area} للمتابعة.",
    reportedDefects: "الأضرار المبلّغ عنها",
    reportDamageTitle: "الإبلاغ عن ضرر",
    photoOfDamage: "صورة الضرر",
    capturePhoto: "التقاط صورة",
    description: "الوصف",
    descriptionPlaceholder: "مثال: خدش على سطح المكتب بطول نحو 10 سم.",
    severity: "درجة الضرر",
    cancel: "إلغاء",
    saveDamage: "حفظ الضرر",
    saveFailed: "تعذر الحفظ",
    describeShort: "يرجى إدخال وصف قصير.",
    signTitle: "التأكيد",
    signSubtitle: "راجع التفاصيل ووقّع رقميًا لإنهاء المحضر.",
    summary: "الملخص",
    object: "العقار",
    roomLabel: "الغرفة",
    date: "التاريخ",
    tenant: "المستأجر",
    operation: "العملية",
    moveIn: "الانتقال إلى السكن",
    moveOut: "مغادرة السكن",
    signature: "التوقيع",
    clear: "مسح",
    signTerms:
      "بتوقيعك تؤكد صحة جميع المعلومات. سيتلقى كل من coliviq والمالك نسخة عبر البريد الإلكتروني. تسري شروط الاستخدام وسياسة الخصوصية.",
    finalizeCta: "إنهاء المحضر",
    pleaseSign: "يرجى التوقيع للمتابعة.",
    doneTitle: "تم إرسال المحضر",
    doneSubtitle: "تم حفظ محضر التسليم وإرسال نسخة عبر البريد الإلكتروني إلى:",
    refLabel: "المرجع",
    downloadPdf: "تنزيل PDF",
    pdfSoon: "سيكون تصدير PDF متاحًا قريبًا.",
    pdfSoonDesc: "سنبلغك حالما يصبح التنزيل متاحًا.",
    toHome: "العودة إلى لوحة التحكم",
    notFound: "لم يتم العثور على المحضر.",
    photoUploadFailed: "تعذر تحميل الصورة",
    photoDeleteFailed: "تعذر حذف الصورة",
    tipStart: "نصيحة قبل البدء",
    tipBody:
      "تأكد من وجود إضاءة جيدة ومساحة تخزين كافية — حجم كل صورة نحو 2 ميغابايت.",
    activeBadge: "نشط",
    doneBadge: "مكتمل",
    statusSubmitted: "تم الإرسال",
    statusDraft: "قيد التنفيذ",
    statusNotStarted: "لم يبدأ بعد",
    statusLocked: "متاح لاحقًا",
    statusExpired: "مقبول تلقائيًا",
    statusClosed: "مغلق",
    submittedOn: "تم الإرسال في {date}",
    lockedUntil: "متاح ابتداءً من {date}",
    lockedHint: "يصبح محضر المغادرة متاحًا قبل 14 يومًا من موعد المغادرة.",
    moveInLockedHint:
      "يصبح محضر الانتقال متاحًا يوم الانتقال ويمكن تعبئته خلال 48 ساعة.",
    moveInExpiredHint:
      "انتهت مهلة 48 ساعة؛ وتُعد الغرفة مقبولة وفقًا لعقد الإيجار.",
    moveOutExpiredHint: "انتهت مهلة المغادرة دون إكمال المحضر.",
    closedHint: "المحضر مغلق أمامك حاليًا.",
    viewPdf: "عرض PDF",
    viewPdfFailed: "تعذر فتح ملف PDF. يرجى المحاولة بعد قليل.",
    loadProtocolFailed:
      "تعذر تحميل محضر التسليم المحفوظ. تحقق من اتصالك وحاول مرة أخرى.",
    noPdfAvailable: "لا يوجد ملف PDF",
    resumeDraft: "متابعة",
    startMoveIn: "بدء محضر الانتقال",
    startMoveOut: "بدء محضر المغادرة",
    actionNeeded: "إجراء مطلوب",
    actionNeededMoveInHint: "أكمل محضر الانتقال · الانتقال في {date}",
    actionNeededMoveOutHint: "أكمل محضر المغادرة · المغادرة في {date}",
    actionNeededMoveInHintNoDate: "أكمل محضر الانتقال",
    actionNeededMoveOutHintNoDate: "أكمل محضر المغادرة",
    actionNeededUpcomingHint: "يفتح في {date} — أكمله يوم انتقالك",
    actionNeededAria: "محضر التسليم يحتاج إلى انتباهك",
    bothDone: "تم كل شيء! أُرسل المحضران.",
    moveInDoneTitle: "محضر الانتقال",
    moveOutDoneTitle: "محضر المغادرة",
    nextStep: "التالي",
    refLabelInline: "مرجع",
    doneTitleKind: "تم إرسال محضر {kind}",
  },
  uk: {
    greeting: "Вітаємо",
    title: "Акт приймання-передачі",
    subtitle: "За кілька кроків зафіксуйте стан кімнати та спільних зон.",
    bulletDuration: "Близько 8–12 хв",
    bulletPause: "Можна призупинити будь-коли",
    bulletStorage: "Фотографії зберігаються безпечно",
    yourHome: "Ваше помешкання",
    fallbackHome: "Ваше помешкання coliviq",
    room: "Кімната",
    noBooking:
      "Активне бронювання не знайдено. Зверніться до служби підтримки.",
    kindMoveIn: "Заселення",
    kindMoveInSub: "Заповніть під час заселення",
    kindMoveOut: "Виселення",
    kindMoveOutSub: "Заповніть перед виїздом",
    badgeRecommended: "РЕКОМЕНДОВАНО СЬОГОДНІ",
    pauseHint: "Можна призупинити будь-коли — ваш прогрес збережеться.",
    cta: "Почати",
    errorCreate: "Не вдалося створити акт",
    pause: "Призупинити",
    back: "Назад",
    continue: "Продовжити",
    step: "Крок",
    of: "з",
    yourProgress: "Ваш прогрес",
    sectionsDone: "Завершено {done}/{total} розділів",
    stepKeys: "Ключі",
    stepPhotos: "Фотографії",
    stepInventory: "Майно",
    stepCommon: "Спільні зони",
    stepSign: "Підпис",
    photosTitle: "Фотографії кімнати",
    photosSubtitle:
      "Сфотографуйте всі стіни, підлогу, вікна та стелю. Щонайменше {min} фото.",
    photosCount: "{n}/{max} фото",
    photosMin: "потрібно мін. {n}",
    photosCta: "Перейти до майна",
    continueHintPhotos: "Додайте ще {n} фото кімнати, щоб продовжити.",
    continueHintKeys: "Додайте щонайменше {n} фото ключів, щоб продовжити.",
    inventoryTitle: "Перевірка майна",
    inventorySubtitle:
      "Для кожного предмета потрібні фотографія та оцінка стану.",
    conditionGood: "Добрий",
    conditionWear: "Сліди зносу",
    conditionDamaged: "Пошкоджений",
    conditionLabel: "Стан",
    conditionChoose: "Вибрати",
    inventoryCta: "Перейти до спільних зон",
    missingConditions: "Не вказано стан для {n} предметів",
    missingPhotos: "Бракує ще {n} фото",
    inventoryCheckedCount: "Перевірено {done}/{total}",
    defectDescription: "Опис пошкодження",
    severityLow: "Незначне",
    severityMedium: "Середнє",
    severityHigh: "Серйозне",
    commonTitle: "Спільні зони",
    commonSubtitle:
      "Щонайменше {min} фото кожної зони — сфотографуйте всі видимі пошкодження.",
    commonAreaKitchen: "Кухня",
    commonAreaBath: "Ванна кімната",
    commonAreaHallway: "Коридор",
    commonAreaOther: "Інше",
    reportDefect: "Повідомити про пошкодження",
    commonAreaCount: "Завершено {n}/{total} зон",
    commonCta: "Перейти до підпису",
    continueHintCommon:
      "Додайте ще {n} фото для зони «{area}», щоб продовжити.",
    reportedDefects: "Зареєстровані пошкодження",
    reportDamageTitle: "Повідомити про пошкодження",
    photoOfDamage: "Фото пошкодження",
    capturePhoto: "Зробити фото",
    description: "Опис",
    descriptionPlaceholder:
      "Наприклад, подряпина на столі довжиною близько 10 см.",
    severity: "Ступінь",
    cancel: "Скасувати",
    saveDamage: "Зберегти пошкодження",
    saveFailed: "Не вдалося зберегти",
    describeShort: "Введіть короткий опис.",
    signTitle: "Підтвердження",
    signSubtitle:
      "Перевірте дані та поставте цифровий підпис, щоб завершити акт.",
    summary: "Підсумок",
    object: "Помешкання",
    roomLabel: "Кімната",
    date: "Дата",
    tenant: "Орендар",
    operation: "Операція",
    moveIn: "Заселення",
    moveOut: "Виселення",
    signature: "Підпис",
    clear: "Очистити",
    signTerms:
      "Підписуючи, ви підтверджуєте правильність усіх даних. coliviq і орендодавець отримають копію електронною поштою. Застосовуються умови користування та політика конфіденційності.",
    finalizeCta: "Завершити акт",
    pleaseSign: "Підпишіть, щоб продовжити.",
    doneTitle: "Акт надіслано",
    doneSubtitle:
      "Акт збережено. Ми надіслали копію електронною поштою на адресу:",
    refLabel: "РЕФ",
    downloadPdf: "Завантажити PDF",
    pdfSoon: "Експорт PDF скоро буде доступний.",
    pdfSoonDesc: "Ми повідомимо, щойно файл буде доступний для завантаження.",
    toHome: "До панелі керування",
    notFound: "Акт не знайдено.",
    photoUploadFailed: "Не вдалося завантажити фото",
    photoDeleteFailed: "Не вдалося видалити фото",
    tipStart: "Порада перед початком",
    tipBody:
      "Подбайте про добре освітлення та перевірте вільне місце — кожне фото займає близько 2 МБ.",
    activeBadge: "Активний",
    doneBadge: "Готово",
    statusSubmitted: "Надіслано",
    statusDraft: "У процесі",
    statusNotStarted: "Ще не розпочато",
    statusLocked: "Буде доступно пізніше",
    statusExpired: "Прийнято автоматично",
    statusClosed: "Закрито",
    submittedOn: "Надіслано {date}",
    lockedUntil: "Доступно з {date}",
    lockedHint: "Акт виселення стане доступним за 14 днів до виїзду.",
    moveInLockedHint:
      "Акт заселення стане доступним у день заселення на 48 годин.",
    moveInExpiredHint:
      "48-годинний термін минув; кімната вважається прийнятою відповідно до договору оренди.",
    moveOutExpiredHint: "Термін виселення минув без заповненого акта.",
    closedHint: "Наразі акт для вас закритий.",
    viewPdf: "Переглянути PDF",
    viewPdfFailed: "Не вдалося відкрити PDF. Спробуйте ще раз за мить.",
    loadProtocolFailed:
      "Не вдалося завантажити збережений протокол. Перевірте з’єднання та повторіть спробу.",
    noPdfAvailable: "PDF відсутній",
    resumeDraft: "Продовжити",
    startMoveIn: "Почати акт заселення",
    startMoveOut: "Почати акт виселення",
    actionNeeded: "Потрібна дія",
    actionNeededMoveInHint: "Заповніть акт заселення · заселення {date}",
    actionNeededMoveOutHint: "Заповніть акт виселення · виселення {date}",
    actionNeededMoveInHintNoDate: "Заповніть акт заселення",
    actionNeededMoveOutHintNoDate: "Заповніть акт виселення",
    actionNeededUpcomingHint: "Відкриється {date} — заповніть у день заселення",
    actionNeededAria: "Акт приймання-передачі потребує вашої уваги",
    bothDone: "Усе готово! Обидва акти надіслано.",
    moveInDoneTitle: "Акт заселення",
    moveOutDoneTitle: "Акт виселення",
    nextStep: "Далі",
    refLabelInline: "Реф",
    doneTitleKind: "Акт «{kind}» надіслано",
  },
  es: {
    greeting: "Hola",
    title: "Acta de entrega",
    subtitle:
      "Documenta el estado de tu habitación y de las zonas comunes en pocos pasos.",
    bulletDuration: "Tarda unos 8–12 min",
    bulletPause: "Puedes pausar en cualquier momento",
    bulletStorage: "Las fotos se guardan de forma segura",
    yourHome: "Tu hogar",
    fallbackHome: "Tu hogar coliviq",
    room: "Habitación",
    noBooking:
      "No se encontró ninguna reserva activa. Ponte en contacto con soporte.",
    kindMoveIn: "Entrada",
    kindMoveInSub: "Complétala al mudarte",
    kindMoveOut: "Salida",
    kindMoveOutSub: "Complétala antes de irte",
    badgeRecommended: "RECOMENDADO HOY",
    pauseHint:
      "Puedes pausar en cualquier momento — tu progreso queda guardado.",
    cta: "Empezar ahora",
    errorCreate: "No se pudo crear el acta",
    pause: "Pausar",
    back: "Atrás",
    continue: "Continuar",
    step: "Paso",
    of: "de",
    yourProgress: "Tu progreso",
    sectionsDone: "{done}/{total} secciones completadas",
    stepKeys: "Llaves",
    stepPhotos: "Fotos",
    stepInventory: "Inventario",
    stepCommon: "Zonas comunes",
    stepSign: "Firma",
    photosTitle: "Fotos de la habitación",
    photosSubtitle:
      "Fotografía todas las paredes, el suelo, las ventanas y el techo. Mínimo {min} fotos.",
    photosCount: "{n}/{max} fotos",
    photosMin: "mín. {n} obligatorias",
    photosCta: "Continuar al inventario",
    continueHintPhotos: "Sube {n} foto(s) más de la habitación para continuar.",
    continueHintKeys: "Sube al menos {n} foto de las llaves para continuar.",
    inventoryTitle: "Revisar el inventario",
    inventorySubtitle: "Cada elemento requiere una foto y su estado.",
    conditionGood: "Bueno",
    conditionWear: "Signos de uso",
    conditionDamaged: "Dañado",
    conditionLabel: "Estado",
    conditionChoose: "Elegir",
    inventoryCta: "Continuar a zonas comunes",
    missingConditions: "Falta indicar {n} estado(s)",
    missingPhotos: "Faltan {n} foto(s)",
    inventoryCheckedCount: "{done}/{total} revisados",
    defectDescription: "Descripción del daño",
    severityLow: "Leve",
    severityMedium: "Medio",
    severityHigh: "Grave",
    commonTitle: "Zonas comunes",
    commonSubtitle:
      "Al menos {min} fotos por zona — fotografía todos los daños visibles.",
    commonAreaKitchen: "Cocina",
    commonAreaBath: "Baño",
    commonAreaHallway: "Pasillo",
    commonAreaOther: "Otra",
    reportDefect: "Informar de un daño",
    commonAreaCount: "{n}/{total} zonas completadas",
    commonCta: "Continuar a la firma",
    continueHintCommon: "Sube {n} foto(s) más de {area} para continuar.",
    reportedDefects: "Daños registrados",
    reportDamageTitle: "Informar de un daño",
    photoOfDamage: "Foto del daño",
    capturePhoto: "Hacer una foto",
    description: "Descripción",
    descriptionPlaceholder:
      "p. ej., arañazo de unos 10 cm en la superficie del escritorio.",
    severity: "Gravedad",
    cancel: "Cancelar",
    saveDamage: "Guardar daño",
    saveFailed: "No se pudo guardar",
    describeShort: "Introduce una descripción breve.",
    signTitle: "Confirmación",
    signSubtitle:
      "Revisa los datos y firma digitalmente para finalizar el acta.",
    summary: "Resumen",
    object: "Propiedad",
    roomLabel: "Habitación",
    date: "Fecha",
    tenant: "Inquilino",
    operation: "Operación",
    moveIn: "Entrada",
    moveOut: "Salida",
    signature: "Firma",
    clear: "Borrar",
    signTerms:
      "Al firmar confirmas que todos los datos son correctos. coliviq y el propietario recibirán una copia por correo electrónico. Se aplican los términos de servicio y la política de privacidad.",
    finalizeCta: "Finalizar el acta",
    pleaseSign: "Firma para continuar.",
    doneTitle: "Acta enviada",
    doneSubtitle:
      "Tu acta se ha guardado. Hemos enviado una copia por correo electrónico a:",
    refLabel: "REF",
    downloadPdf: "Descargar PDF",
    pdfSoon: "La exportación a PDF estará disponible pronto.",
    pdfSoonDesc: "Te avisaremos cuando esté disponible para descargar.",
    toHome: "Volver al panel",
    notFound: "No se encontró el acta.",
    photoUploadFailed: "No se pudo subir la foto",
    photoDeleteFailed: "No se pudo eliminar la foto",
    tipStart: "Consejo antes de empezar",
    tipBody:
      "Asegúrate de tener buena iluminación y comprueba el almacenamiento — cada foto ocupa unos 2 MB.",
    activeBadge: "Activo",
    doneBadge: "Completado",
    statusSubmitted: "Enviado",
    statusDraft: "En curso",
    statusNotStarted: "Aún no iniciado",
    statusLocked: "Disponible más adelante",
    statusExpired: "Aceptado automáticamente",
    statusClosed: "Cerrado",
    submittedOn: "Enviado el {date}",
    lockedUntil: "Disponible desde el {date}",
    lockedHint:
      "El acta de salida estará disponible 14 días antes de tu salida.",
    moveInLockedHint:
      "El acta de entrada estará disponible el día de tu llegada durante 48 horas.",
    moveInExpiredHint:
      "El plazo de 48 horas ha terminado; la habitación se considera aceptada según tu contrato de alquiler.",
    moveOutExpiredHint: "El plazo de salida terminó sin un acta completada.",
    closedHint: "El acta está cerrada para ti en este momento.",
    viewPdf: "Ver PDF",
    viewPdfFailed:
      "No se pudo abrir el PDF. Inténtalo de nuevo en unos instantes.",
    loadProtocolFailed:
      "No se pudo cargar el protocolo guardado. Comprueba tu conexión e inténtalo de nuevo.",
    noPdfAvailable: "No hay PDF disponible",
    resumeDraft: "Continuar",
    startMoveIn: "Iniciar acta de entrada",
    startMoveOut: "Iniciar acta de salida",
    actionNeeded: "Acción necesaria",
    actionNeededMoveInHint: "Completa tu acta de entrada · entrada el {date}",
    actionNeededMoveOutHint: "Completa tu acta de salida · salida el {date}",
    actionNeededMoveInHintNoDate: "Completa tu acta de entrada",
    actionNeededMoveOutHintNoDate: "Completa tu acta de salida",
    actionNeededUpcomingHint:
      "Se abre el {date} — complétala el día de tu llegada",
    actionNeededAria: "El acta de entrega necesita tu atención",
    bothDone: "¡Todo listo! Se han enviado las dos actas.",
    moveInDoneTitle: "Acta de entrada",
    moveOutDoneTitle: "Acta de salida",
    nextStep: "Siguiente",
    refLabelInline: "Ref",
    doneTitleKind: "Acta de {kind} enviada",
  },
};

export const ALL_PROTOCOL_COPY: Record<SupportedLanguage, ProtocolCopy> = {
  de: PROTOCOL_COPY.de,
  en: PROTOCOL_COPY.en,
  ...ADDITIONAL_PROTOCOL_COPY,
};

export function getProtocolCopy(language: SupportedLanguage): ProtocolCopy {
  return ALL_PROTOCOL_COPY[language];
}
